import React from 'react';
import { ApReels, VinkReels } from '@schibsted-regional/components';

import type { ApReelsProps } from '@schibsted-regional/components';
import type { SvpAsset } from '../../../../core/bindings/data/fetch-stream-data.js';

import { isMobileApp } from '../../../../core/utils/is-mobile.js';
import { useReelsOverlay } from '../../../../regional/rendering/blocks/Reels/useReelsOverlay.js';
import { usePulseContext } from '../../../../core/mandatory-integrations/pulse/PulseProvider.js';

type CommonProps = {
  title: string;
  playlistId: string;
  reels: SvpAsset[];
  url: string;
};

type VinkProps = CommonProps & {
  brand: 'vink';
};

type ApProps = CommonProps & {
  brand: 'ap';
  rowNumberInt: number;
};

const ReelsSlider = (props: VinkProps | ApProps) => {
  const { title, reels, playlistId, url, brand } = props;
  const { trackClickUIElement } = usePulseContext();

  const preloadUrls = reels.map(
    ({ id }) => `${url}/stories/${id}?playlistId=${playlistId}`,
  );

  const previews = reels.map((video, index) => {
    const { metadata } = video.additional;

    return {
      id: video.id,
      title: video.title,
      link: `${url}/stories/${video.id}?playlistId=${playlistId}`,
      duration: Number(video.duration),
      imagePreviewUrl: video.images.main,
      videoPreviewUrl: `${
        metadata?.preview_vivi_category ||
        metadata?.preview_360p ||
        metadata?.preview ||
        metadata?.preview_hq
      }`,
      'data-pulse-entity-id': `reel-${video.id}`,
      'data-pulse-entity-name': `Reel ${video.id}: ${video.title}`,
      'data-pulse-position-in-bundle': `${index + 1}`,
      'data-pulse-source': 'reels-widget',
      'data-pulse-product-name': 'reels-widget',
      ...(brand === 'ap' && {
        'data-pulse-position': `${props.rowNumberInt}`,
        'data-pulse-product-name': `reels-widget:reelname:${title}`,
      }),
    };
  });

  const { openReel } = useReelsOverlay({ preloadItems: preloadUrls });

  const onPreviewClick: ApReelsProps['onPreviewClick'] = (event, video) => {
    const { pulsePosition, pulsePositionInBundle } =
      event.currentTarget.parentElement?.dataset ?? {};

    trackClickUIElement({
      object: {
        id: `reel-${video.id}`,
        name: `Reel video-${video.id}`,
        elementType: 'Teaser',
        placement: {
          primaryPosition: Number(pulsePosition),
          positionInBundle: Number(pulsePositionInBundle),
        },
      },
    });

    const useIframeOverlay = !isMobileApp();

    if (useIframeOverlay) {
      event.preventDefault();
      openReel(video.link);
    }
  };

  const commonProps: ApReelsProps = { title, previews, onPreviewClick };

  const reelsCarousel = getBrandReels(brand, commonProps);

  return (
    <div
      data-pulse-entity-id={`${brand}-reels-${playlistId}`}
      data-pulse-entity-name={`${brand} reels sliders`}
      data-pulse-entity-type="Reel"
      {...(brand === 'ap' && {
        'data-pulse-position': `${props.rowNumberInt}`,
        'data-pulse-product-name': `reels-widget:reelname:${title}`,
      })}
      className="reels-widget"
    >
      {reelsCarousel}
    </div>
  );
};

const getBrandReels = (brand: 'vink' | 'ap', commonProps: ApReelsProps) => {
  if (brand === 'ap') {
    return <ApReels {...commonProps} />;
  }

  return <VinkReels {...commonProps} />;
};

export { ReelsSlider };
